/**
 * Filter variant config for the Advanced Table view.
 * Maps Frappe field types to filter variants and provides
 * user-friendly operator labels per variant.
 */
import type { FilterOperator } from '@/lib/erp/filterTypes';

// ---------------------------------------------------------------------------
// Filter variants
// ---------------------------------------------------------------------------

export type FilterVariant = 'text' | 'number' | 'date' | 'select' | 'boolean';

const COLUMN_TYPE_TO_VARIANT: Record<string, FilterVariant> = {
  Data: 'text',
  'Small Text': 'text',
  Text: 'text',
  'Long Text': 'text',
  Code: 'text',
  Int: 'number',
  Float: 'number',
  Currency: 'number',
  Rating: 'number',
  Check: 'boolean',
  Select: 'select',
  Link: 'select',
  Date: 'date',
  Datetime: 'date',
  Time: 'date',
  Color: 'select',
};

export function getFilterVariant(columnType: string | undefined): FilterVariant {
  if (!columnType) return 'text';
  return COLUMN_TYPE_TO_VARIANT[columnType] ?? 'text';
}

// ---------------------------------------------------------------------------
// Operator labels per variant (user-friendly)
// ---------------------------------------------------------------------------

interface OperatorOption {
  value: FilterOperator;
  label: string;
}

const TEXT_OPERATORS: OperatorOption[] = [
  { value: 'like', label: 'Contains' },
  { value: 'equals', label: 'Is' },
  { value: 'notEquals', label: 'Is not' },
  { value: 'notLike', label: 'Does not contain' },
  { value: 'isSet', label: 'Is not empty' },
  { value: 'isNotSet', label: 'Is empty' },
];

const NUMBER_OPERATORS: OperatorOption[] = [
  { value: 'equals', label: 'Equals' },
  { value: 'notEquals', label: 'Not equals' },
  { value: 'greaterThan', label: 'Greater than' },
  { value: 'lessThan', label: 'Less than' },
  { value: 'greaterOrEqual', label: 'Greater or equal' },
  { value: 'lessOrEqual', label: 'Less or equal' },
  { value: 'between', label: 'Between' },
  { value: 'isSet', label: 'Is not empty' },
  { value: 'isNotSet', label: 'Is empty' },
];

const DATE_OPERATORS: OperatorOption[] = [
  { value: 'equals', label: 'Is' },
  { value: 'notEquals', label: 'Is not' },
  { value: 'greaterThan', label: 'After' },
  { value: 'lessThan', label: 'Before' },
  { value: 'between', label: 'Between' },
  { value: 'isSet', label: 'Is not empty' },
  { value: 'isNotSet', label: 'Is empty' },
];

const SELECT_OPERATORS: OperatorOption[] = [
  { value: 'equals', label: 'Is' },
  { value: 'notEquals', label: 'Is not' },
  { value: 'in', label: 'Is any of' },
  { value: 'notIn', label: 'Is none of' },
  { value: 'isSet', label: 'Is not empty' },
  { value: 'isNotSet', label: 'Is empty' },
];

const BOOLEAN_OPERATORS: OperatorOption[] = [
  { value: 'equals', label: 'Is' },
  { value: 'notEquals', label: 'Is not' },
];

const OPERATORS_BY_VARIANT: Record<FilterVariant, OperatorOption[]> = {
  text: TEXT_OPERATORS,
  number: NUMBER_OPERATORS,
  date: DATE_OPERATORS,
  select: SELECT_OPERATORS,
  boolean: BOOLEAN_OPERATORS,
};

export function getOperatorsForVariant(variant: FilterVariant): OperatorOption[] {
  return OPERATORS_BY_VARIANT[variant];
}

/** Operators that require no value input */
export const NO_VALUE_OPERATORS: FilterOperator[] = ['isSet', 'isNotSet'];

/** Operators that need two value inputs */
export const DUAL_VALUE_OPERATORS: FilterOperator[] = ['between'];
